import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContact } from "@/context/ContactContext";
import { PRODUCTS } from "@/data/products";
import ProductCard from "@/components/ProductCard";

export default function Catalog() {
  const { t } = useLang();
  const { openContact } = useContact();
  const [query, setQuery] = useState("");

  const norm = (s) => (s || "").toString().toLowerCase().replace(/[\s\-/.]/g, "");
  const q = norm(query.trim());

  const filtered = useMemo(() => {
    if (!q) return PRODUCTS;
    return PRODUCTS.filter((p) => {
      const parts = [
        p.ref,
        p.category,
        ...(p.oem || []),
        ...(p.specs || []),
        ...(p.name ? Object.values(p.name) : []),
        ...(p.desc ? Object.values(p.desc) : []),
        ...(p.tech ? Object.values(p.tech) : []),
      ];
      const haystack = norm(parts.join(" "));
      return haystack.includes(q);
    });
  }, [q]);

  const hasResults = filtered.length > 0;

  return (
    <section
      id="catalog"
      data-testid="catalog-section"
      className="relative bg-[#0B1712] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-10"
        >
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[color:var(--dp-orange)] mb-4">
            {t.catalog.overline}
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white">
            {t.catalog.title}
          </h2>
          <p className="mt-5 text-white/60 font-light text-lg">{t.catalog.sub}</p>
        </motion.div>

        {/* OEM search */}
        <div className="mb-14 max-w-2xl">
          <label
            htmlFor="oem-search"
            className="block font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3"
          >
            {t.catalog.searchLabel}
          </label>
          <div className="relative flex items-center border border-white/15 bg-[#112318] focus-within:border-[color:var(--dp-orange)] transition-colors">
            <Search size={18} className="ml-4 text-white/40 shrink-0" />
            <input
              id="oem-search"
              data-testid="oem-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.catalog.searchPlaceholder}
              className="w-full bg-transparent px-3 py-4 text-white placeholder:text-white/35 focus:outline-none text-sm font-mono"
            />
            {query && (
              <button
                data-testid="oem-search-clear"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="mr-3 flex items-center gap-1 text-white/50 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors"
              >
                <X size={14} /> {t.catalog.clear}
              </button>
            )}
          </div>
          {query && (
            <p
              data-testid="oem-search-count"
              className="mt-3 font-mono text-xs text-white/50"
            >
              {filtered.length} {t.catalog.results}
            </p>
          )}
        </div>

        {hasResults ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} query={q} />
            ))}
          </div>
        ) : (
          <div
            data-testid="oem-no-results"
            className="border border-white/10 bg-[#112318] p-10 sm:p-14 text-center"
          >
            <p className="text-white/70 text-lg font-light max-w-lg mx-auto">
              {t.catalog.noResults}
            </p>
            <button
              data-testid="no-results-contact"
              onClick={() => openContact(null)}
              className="mt-6 inline-flex items-center px-6 py-3 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold text-sm transition-colors"
            >
              {t.nav.cta}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
